import { Stack, Typography } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { useQuery } from "@tanstack/react-query";
import { itemBinsApi } from "@/api/endpoints";

/**
 * The item's bin in a given warehouse — "where to walk to". Shown wherever someone physically
 * handles stock: picking for an issue, putting away a receipt.
 *
 * Renders nothing when no bin is mapped or the item/warehouse isn't chosen yet. A missing bin is
 * normal for a new item and must not clutter the line; the point of showing it is to save a walk,
 * not to nag about master data.
 */
export function BinHint({
  itemId, warehouseId, label = "Bin",
}: {
  itemId?: string | null;
  warehouseId?: string | null;
  /** Leading word — "Pick from" for issues, "Put away in" for receipts. */
  label?: string;
}) {
  const enabled = !!itemId?.trim() && !!warehouseId?.trim();
  const q = useQuery({
    queryKey: ["item-bin", itemId, warehouseId],
    queryFn: () => itemBinsApi.list({ warehouseId: warehouseId!, itemId: itemId!, pageSize: 10 }),
    enabled,
    // A typed-in id that isn't a real item 404s; that's not worth retrying or surfacing.
    retry: false,
    staleTime: 5 * 60_000,
  });

  // Primary first; fall back to any mapped bin so a part-configured item still helps.
  const bin = q.data?.items.find(b => b.isPrimary) ?? q.data?.items[0];
  if (!bin) return null;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <PlaceIcon sx={{ fontSize: 14 }} color="action" />
      <Typography variant="caption" color="text.secondary">
        {label} <b>{bin.locationCode}</b>
        {bin.locationName ? ` · ${bin.locationName}` : ""}
        {bin.isPrimary ? "" : " (not primary)"}
      </Typography>
    </Stack>
  );
}
